import { apiRequest, apiRoutes } from '../../api';
import React, { useState, useEffect, useRef } from 'react';
import { dataUrlToBlob } from '../../utils/dataUrl';
import appConfig from '../../config/appConfig';
import './PropertyWizard.css';

function FloorPlanStep({ onSubmit, initialData = {}, onPreviousStep, propertyId }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedImageUrl, setSavedImageUrl] = useState(null);
  const [isFloorPlanSaved, setIsFloorPlanSaved] = useState(false);
  const [isCheckingExisting, setIsCheckingExisting] = useState(true);
  const [showGuide, setShowGuide] = useState(false);
  const iframeRef = useRef(null);
  const externalEditorRef = useRef(null);
  const floorPlanEditorUrl = appConfig.services.floorPlanEditorURL;
  const floorPlanEditorOrigin = new URL(floorPlanEditorUrl, window.location.origin).origin;
  const editorUrl = new URL(floorPlanEditorUrl, window.location.origin);
  editorUrl.searchParams.set('property_id', propertyId || '');

  // Check for existing saved image when component mounts
  useEffect(() => {
    const checkExistingImage = async () => {
      // First check if we have saved data from initialData
      if (initialData.floor_plan_saved && initialData.floor_plan_image_url) {
        setSavedImageUrl(initialData.floor_plan_image_url);
        setIsFloorPlanSaved(true);
        setIsCheckingExisting(false);
        return;
      }

      // Fetch from Django API to get the actual saved plan
      try {
        const token = localStorage.getItem('access_token');
        const response = await apiRequest(apiRoutes.properties.detail(propertyId), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.plan && data.plan.url) {
            setSavedImageUrl(data.plan.url);
            setIsFloorPlanSaved(true);

          }
        }
      } catch (err) {

      } finally {
        setIsCheckingExisting(false);
      }
    };

    if (propertyId) {
      checkExistingImage();
    }
  }, [propertyId, initialData]);

  // Helper function to get GeoJSON data from iframe (without saving)
  const saveGeoJSONData = async () => {
    return new Promise((resolve, reject) => {
      if (!iframeRef.current || !iframeRef.current.contentWindow) {
        reject(new Error('Iframe not available'));
        return;
      }

      const iframeWindow = iframeRef.current.contentWindow;

      // Request GeoJSON export
      iframeWindow.postMessage(
        { type: 'export-geojson', propertyId: propertyId },
        floorPlanEditorOrigin
      );

      let messageHandler = null;
      let timeout = null;

      const cleanup = () => {
        if (timeout) clearTimeout(timeout);
        if (messageHandler) window.removeEventListener('message', messageHandler);
      };

      timeout = setTimeout(() => {
        cleanup();
        reject(new Error('GeoJSON export timeout'));
      }, 10000);

      messageHandler = (event) => {
        if (event.origin !== floorPlanEditorOrigin) return;

        if (event.data && event.data.type === 'geojson-exported') {
          cleanup();

          if (event.data.success && event.data.geoJsonData) {
            // Just return the data, don't save it separately
            resolve(event.data.geoJsonData);
          } else {
            reject(new Error(event.data.error || 'Failed to export GeoJSON'));
          }
        }
      };

      window.addEventListener('message', messageHandler);
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      if (!iframeRef.current || !iframeRef.current.contentWindow) {
        throw new Error('ویرایشگر پلان در دسترس نیست');
      }

      const iframeWindow = iframeRef.current.contentWindow;

      // Step 1: Ask iframe to save to its location first
      iframeWindow.postMessage(
        { type: 'save-floorplan' },
        floorPlanEditorOrigin
      );

      // Wait for iframe to save
      await new Promise(resolve => setTimeout(resolve, 3000));

      // Step 2: Try to get canvas data from iframe
      let imageBlob = null;
      let dataUrl = null;

      try {
        const canvasDataPromise = new Promise((resolve, reject) => {
          let messageHandler = null;
          let timeout = null;

          const cleanup = () => {
            if (timeout) clearTimeout(timeout);
            if (messageHandler) window.removeEventListener('message', messageHandler);
          };

          timeout = setTimeout(() => {
            cleanup();
            reject(new Error('Timeout'));
          }, 5000);

          messageHandler = (event) => {
            if (event.origin !== floorPlanEditorOrigin) return;

            if (event.data && event.data.type === 'canvas-data') {
              cleanup();
              if (event.data.success && event.data.dataUrl) {
                resolve(event.data.dataUrl);
              } else {
                reject(new Error('No data'));
              }
            }
          };

          window.addEventListener('message', messageHandler);
        });

        // Request canvas data
        iframeWindow.postMessage(
          { type: 'get-canvas-data' },
          floorPlanEditorOrigin
        );

        dataUrl = await canvasDataPromise;
        imageBlob = await dataUrlToBlob(dataUrl);

      } catch (err) {

        throw new Error('لطفاً مطمئن شوید که پلان را ترسیم کرده‌اید. اگر ترسیم کردید، دوباره دکمه ذخیره را در ویرایشگر بزنید.');
      }

      if (!imageBlob) {
        throw new Error('تصویر پلان دریافت نشد');
      }

      // Step 4: Get GeoJSON data
      let geoJsonData = null;
      try {
        geoJsonData = await saveGeoJSONData();

      } catch (err) {

      }

      // Step 5: Create files
      const imageFile = new File([imageBlob], `floorplan-${propertyId}.png`, { 
        type: 'image/png' 
      });

      let jsonFile = null;
      if (geoJsonData) {
        const jsonData = {
          propertyId: propertyId,
          exportDate: new Date().toISOString(),
          floorplan: geoJsonData
        };

        const jsonBlob = new Blob([JSON.stringify(jsonData, null, 2)], { 
          type: 'application/json' 
        });

        jsonFile = new File([jsonBlob], `floor-plan-data-${propertyId}.json`, {
          type: 'application/json'
        });
      }

      // Step 6: Upload to Django (saves to media/properties/{property_id}/floor-plans/)
      const formData = new FormData();
      formData.append('plan', imageFile);
      if (jsonFile) {
        formData.append('graph_path', jsonFile);
      }

      const token = localStorage.getItem('access_token');
      const uploadResponse = await apiRequest(apiRoutes.propertyWizard.plan(propertyId), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json();
        throw new Error(errorData.detail || errorData.error || 'خطا در ذخیره پلان');
      }

      // Step 7: Fetch the actual saved URL from Django API
      const propertyResponse = await apiRequest(apiRoutes.properties.detail(propertyId), {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      let finalImageUrl = dataUrl; // Fallback to data URL

      if (propertyResponse.ok) {
        const propertyData = await propertyResponse.json();
        if (propertyData.plan && propertyData.plan.url) {
          finalImageUrl = propertyData.plan.url;

        }
      }

      // Step 8: Set preview image
      setSavedImageUrl(finalImageUrl);
      setIsFloorPlanSaved(true);
      setError('موفق: پلان و داده‌های JSON در media/properties/' + propertyId + '/floor-plans/ ذخیره شد.');
    } catch (err) {

      setError(err.message || 'خطا در ذخیره پلان. لطفاً دوباره تلاش کنید.');
    } finally {
      setSaving(false);
    }
  };

  const handleCheckSavedPlan = async () => {
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      const response = await apiRequest(apiRoutes.properties.detail(propertyId), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.plan && data.plan.url) {
          setSavedImageUrl(data.plan.url);
          setIsFloorPlanSaved(true);
          setError('موفق: پلان یافت و بارگذاری شد.');
        } else {
          setError('هیچ پلان ذخیره شده‌ای یافت نشد. لطفاً ابتدا پلان را ذخیره کنید.');
        }
      } else {
        setError('خطا در بارگذاری اطلاعات ملک');
      }
    } catch (err) {

      setError('خطا در بررسی پلان ذخیره شده');
    }
  };

  const handleContinue = async () => {
    setLoading(true);
    setError(null);

    try {

      // Check if floor plan is saved
      if (!savedImageUrl) {
        setError('لطفاً ابتدا پلان را ذخیره کنید');
        setLoading(false);
        return;
      }

      // The files are already saved to Django/MinIO via handleSave
      // Just move to the next step
      onSubmit({
        floor_plan_saved: true,
        floor_plan_image_url: savedImageUrl,
        completed: true
      });
    } catch (err) {

      setError(err.message || 'خطا در ادامه');
      setLoading(false);
    }
  };

  const handleDeleteAndRedraw = async () => {
    setSaving(true);
    setError(null);

    // Delete from MinIO using Django backend API (avoids CORS issues)
    try {
      const token = localStorage.getItem('access_token');
      const response = await apiRequest(apiRoutes.propertyWizard.deletePlan(propertyId), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {

      } else {

      }
    } catch (error) {

      // Don't fail the operation - just continue with reset
    }

    // Reset the state to allow new drawing (regardless of delete success)
    setIsFloorPlanSaved(false);
    setSavedImageUrl(null);
    setError('پلان قبلی حذف شد. می‌توانید پلان جدید ترسیم کنید.');

    // Send message to iframe to reset
    if (iframeRef.current && iframeRef.current.contentWindow) {
      try {
        iframeRef.current.contentWindow.postMessage(
          { type: 'reset-floorplan' },
          floorPlanEditorOrigin
        );
      } catch (error) {

      }
    }

    setSaving(false);
  };

  const handleExportGeoJSON = async () => {
    setSaving(true);
    setError(null);

    try {
      // Send message to iframe to export GeoJSON data
      if (iframeRef.current && iframeRef.current.contentWindow) {
        const iframeWindow = iframeRef.current.contentWindow;

        // Send export request
        iframeWindow.postMessage(
          { type: 'export-geojson', propertyId: propertyId },
          floorPlanEditorOrigin
        );

        // Wait for response with timeout
        const exportPromise = new Promise((resolve, reject) => {
          let messageHandler = null;
          let timeout = null;

          const cleanup = () => {
            if (timeout) {
              clearTimeout(timeout);
            }
            if (messageHandler) {
              window.removeEventListener('message', messageHandler);
            }
          };

          timeout = setTimeout(() => {
            cleanup();
            reject(new Error('Export timeout'));
          }, 10000); // 10 second timeout

          messageHandler = (event) => {
            // Only accept messages from the iframe origin
            if (event.origin !== floorPlanEditorOrigin) {
              return;
            }

            if (event.data && event.data.type === 'geojson-exported') {
              cleanup();

              if (event.data.success) {
                resolve(event.data);
              } else {
                reject(new Error(event.data.error || 'Failed to export GeoJSON'));
              }
            }
          };

          window.addEventListener('message', messageHandler);
        });

        const result = await exportPromise;

        if (result.geoJsonData) {
          // Create download for the complete GeoJSON export
          const exportData = {
            propertyId: propertyId,
            exportDate: new Date().toISOString(),
            floorplan: result.geoJsonData
          };

          // Download as single JSON file
          const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `floorplan-${propertyId}-export.json`;
          link.click();
          URL.revokeObjectURL(url);

          setError('موفق: فایل JSON دانلود شد و برای تحلیل معماری آماده است.');
        }
      }
    } catch (err) {

      setError('خطا در خروجی JSON. لطفاً دوباره تلاش کنید.');
    } finally {
      setSaving(false);
    }
  };

  const openEditorInNewTab = () => {
    const standaloneUrl = new URL(editorUrl.toString());
    standaloneUrl.searchParams.set('standalone', '1');
    externalEditorRef.current = window.open(
      standaloneUrl.toString(),
      'saita-floor-plan-editor'
    );
    if (externalEditorRef.current) {
      externalEditorRef.current.focus();
    } else {
      setError('مرورگر باز شدن تب جدید را مسدود کرده است. لطفاً Pop-up را برای این سایت فعال کنید.');
    }
  };

  useEffect(() => {
    const handleEditorSubmission = async (event) => {
      if (
        event.origin !== floorPlanEditorOrigin ||
        !event.data ||
        event.data.type !== 'floorplan-editor-submit'
      ) {
        return;
      }

      const sourceWindow = event.source;
      const reply = (payload) => {
        if (sourceWindow && typeof sourceWindow.postMessage === 'function') {
          sourceWindow.postMessage({ type: 'floorplan-editor-save-result', ...payload }, event.origin);
        }
      };

      if (String(event.data.propertyId || '') !== String(propertyId || '')) {
        reply({ success: false, error: 'شناسه ملک با فرم جاری مطابقت ندارد' });
        return;
      }

      if (!event.data.dataUrl) {
        reply({ success: false, error: 'تصویر پلان دریافت نشد' });
        return;
      }

      setSaving(true);
      setError(null);

      try {
        const imageBlob = await dataUrlToBlob(event.data.dataUrl);
        const formData = new FormData();
        formData.append('plan', new File([imageBlob], `floorplan-${propertyId}.png`, { type: 'image/png' }));

        if (event.data.floorplanData) {
          const graphBlob = new Blob([
            JSON.stringify({
              propertyId,
              exportDate: new Date().toISOString(),
              floorplan: event.data.floorplanData,
            }, null, 2)
          ], { type: 'application/json' });
          formData.append('graph_path', new File([graphBlob], `floor-plan-data-${propertyId}.json`, { type: 'application/json' }));
        }

        const token = localStorage.getItem('access_token');
        const uploadResponse = await apiRequest(apiRoutes.propertyWizard.plan(propertyId), {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData,
        });

        if (!uploadResponse.ok) {
          let message = 'خطا در ذخیره پلان';
          try {
            const responseData = await uploadResponse.json();
            message = responseData.detail || responseData.error || message;
          } catch (_) {
            // Keep the user-friendly fallback message.
          }
          throw new Error(message);
        }

        let finalImageUrl = event.data.dataUrl;
        const propertyResponse = await apiRequest(apiRoutes.properties.detail(propertyId), {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (propertyResponse.ok) {
          const propertyData = await propertyResponse.json();
          if (propertyData.plan && propertyData.plan.url) {
            finalImageUrl = propertyData.plan.url;
          }
        }

        setSavedImageUrl(finalImageUrl);
        setIsFloorPlanSaved(true);
        setError('موفق: پلان در فرم ملک ذخیره شد.');

        // Live draft sync normally updates this iframe already. Sending the
        // saved model directly also supports browsers without BroadcastChannel.
        if (
          event.data.floorplanData &&
          iframeRef.current &&
          iframeRef.current.contentWindow &&
          iframeRef.current.contentWindow !== sourceWindow
        ) {
          iframeRef.current.contentWindow.postMessage({
            type: 'load-floorplan',
            floorplanData: event.data.floorplanData,
          }, floorPlanEditorOrigin);
        }
        reply({ success: true, imageUrl: finalImageUrl });
      } catch (submissionError) {
        const message = submissionError.message || 'ذخیره پلان ناموفق بود';
        setError(message);
        reply({ success: false, error: message });
      } finally {
        setSaving(false);
      }
    };

    window.addEventListener('message', handleEditorSubmission);
    return () => window.removeEventListener('message', handleEditorSubmission);
  }, [floorPlanEditorOrigin, propertyId]);

  return (
    <div className="floor-plan-step">
      <div className="step-header" style={{ flexShrink: 0 }}>
        <h3>طراحی پلان واحد</h3>
        <p>پلان واحد خود را طراحی کنید و آن را ذخیره کنید</p>
      </div>

      {error && (
        <div className={error.startsWith('موفق:') ? 'success-message' : 'error-message'} style={{ flexShrink: 0 }}>
          {error.replace(/^موفق:\s*/, '')}
        </div>
      )}

      {isCheckingExisting && (
        <div className="checking-existing-message" style={{ flexShrink: 0 }}>
          در حال بررسی پلان‌های ذخیره شده...
        </div>
      )}

      {/* Three Column Layout */}
      <div className="floor-plan-layout">

        {/* Column 1: Instructions */}
        <div className={`floor-plan-instructions ${showGuide ? 'is-open' : 'is-collapsed'}`}>
          <div className="instructions-header">
            <h4 style={{ margin: '0 0 15px 0', fontSize: '16px' }}>راهنمای استفاده</h4>
            <button type="button" className="close-guide-button" onClick={() => setShowGuide(false)} aria-label="بستن راهنما">×</button>
          </div>
          <div className="instructions-content">
            <div className="instruction-step" style={{ marginBottom: '15px' }}>
              <div style={{ display: 'flex', alignItems: 'start', gap: '10px' }}>
                <div style={{ 
                  backgroundColor: '#007bff', 
                  color: 'white', 
                  borderRadius: '50%', 
                  width: '24px', 
                  height: '24px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  flexShrink: 0,
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>1</div>
                <div>
                  <strong style={{ fontSize: '14px' }}>طراحی پلان</strong>
                  <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#666' }}>از ابزارهای ترسیم استفاده کنید</p>
                </div>
              </div>
            </div>
            <div className="instruction-step" style={{ marginBottom: '15px' }}>
              <div style={{ display: 'flex', alignItems: 'start', gap: '10px' }}>
                <div style={{ 
                  backgroundColor: '#007bff', 
                  color: 'white', 
                  borderRadius: '50%', 
                  width: '24px', 
                  height: '24px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  flexShrink: 0,
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>2</div>
                <div>
                  <strong style={{ fontSize: '14px' }}>افزودن عناصر</strong>
                  <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#666' }}>درب، پنجره و برچسب اضافه کنید</p>
                </div>
              </div>
            </div>
            <div className="instruction-step" style={{ marginBottom: '15px' }}>
              <div style={{ display: 'flex', alignItems: 'start', gap: '10px' }}>
                <div style={{ 
                  backgroundColor: '#007bff', 
                  color: 'white', 
                  borderRadius: '50%', 
                  width: '24px', 
                  height: '24px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  flexShrink: 0,
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>3</div>
                <div>
                  <strong style={{ fontSize: '14px' }}>ذخیره</strong>
                  <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#666' }}>پلان را ذخیره کنید</p>
                </div>
              </div>
            </div>
          </div>
          <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #dee2e6' }}>
            <h5 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>نکات</h5>
            <ul style={{ margin: 0, paddingRight: '20px', fontSize: '12px', color: '#666' }}>
              <li style={{ marginBottom: '8px' }}>برای فضای بیشتر در تب جدید باز کنید</li>
              <li>پلان با شناسه ملک ذخیره می‌شود</li>
            </ul>
          </div>
        </div>

        {/* Column 2: Action Buttons */}
        <div className="floor-plan-actions">
          <button
            type="button"
            onClick={() => setShowGuide(true)}
            className="guide-button"
          >
            راهنمای سریع
          </button>

          <button
            type="button"
            onClick={handleSave}
            className="save-button"
            disabled={saving}
            style={{ 
              padding: '10px',
              fontSize: '13px',
              whiteSpace: 'nowrap',
              width: '100%'
            }}
          >
            {saving ? 'در حال ذخیره...' : (isFloorPlanSaved ? 'ذخیره مجدد' : 'ذخیره')}
          </button>

          {isFloorPlanSaved && (
            <button
              type="button"
              onClick={handleExportGeoJSON}
              className="export-geojson-button"
              disabled={saving}
              style={{ 
                padding: '10px',
                fontSize: '13px',
                whiteSpace: 'nowrap',
                width: '100%'
              }}
            >
              دانلود JSON
            </button>
          )}

          {isFloorPlanSaved && (
            <button
              type="button"
              onClick={handleDeleteAndRedraw}
              className="delete-redraw-button"
              disabled={saving}
              style={{ 
                padding: '10px',
                fontSize: '13px',
                whiteSpace: 'nowrap',
                width: '100%'
              }}
            >
              {saving ? 'در حال حذف...' : 'حذف و ترسیم مجدد'}
            </button>
          )}

          {!isFloorPlanSaved && (
            <button
              type="button"
              onClick={handleCheckSavedPlan}
              className="check-plan-button"
              disabled={saving}
              style={{ 
                padding: '10px',
                fontSize: '13px',
                whiteSpace: 'nowrap',
                width: '100%'
              }}
            >
              بررسی پلان ذخیره شده
            </button>
          )}

          {/* Saved Floor Plan Preview - Compact */}
          {isFloorPlanSaved && savedImageUrl && (
            <div style={{ 
              marginTop: '20px',
              padding: '10px',
              backgroundColor: '#e7f5e7',
              borderRadius: '8px',
              border: '1px solid #28a745'
            }}>
              <h5 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#28a745' }}>پلان ذخیره شده</h5>
              <img 
                src={savedImageUrl} 
                alt="Saved Floor Plan" 
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: '4px',
                  border: '1px solid #dee2e6'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}
        </div>

        {/* Column 3: Floor Plan Editor (Largest) */}
        <div className="floor-plan-editor">
          <div style={{ 
            padding: '10px 15px',
            backgroundColor: '#f8f9fa',
            borderBottom: '1px solid #dee2e6',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0
          }}>
            <h4 style={{ margin: 0, fontSize: '16px' }}>ویرایشگر پلان</h4>
            <button
              type="button"
              onClick={openEditorInNewTab}
              className="open-editor-button"
              style={{
                padding: '5px 10px',
                fontSize: '12px',
                backgroundColor: '#007bff',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '4px'
              }}
            >
              باز کردن در تب جدید
            </button>
          </div>
          <div className="floor-plan-frame-wrap">
            <iframe
              ref={iframeRef}
              src={editorUrl.toString()}
              style={{ 
                width: '100%', 
                height: '100%', 
                border: 'none'
              }}
              title="Floor Plan Editor"
            ></iframe>
          </div>
        </div>
      </div>

      <div className="navigation-buttons">
        <button type="button" onClick={handleContinue} className="submit-button" disabled={loading}>
          {loading ? 'در حال بارگذاری...' : 'تأیید و ادامه'}
        </button>
        <button type="button" onClick={onPreviousStep} className="previous-button" disabled={loading}>
          بازگشت
        </button>
      </div>
    </div>
  );
}

export default FloorPlanStep;
